import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, Form, Input, Button, Select, Alert, message, Spin } from 'antd'
import { useDeepEffect } from '../../utils/hooks'
import { debounce } from 'lodash'
import {
  grantPointManually,
  searchUserPoint,
  fetchPointScopeList
} from '../../modules/reward'

/**
 * แจกพอยท์เอง (spec 013 P1)
 *
 * เดิมแอดมินมีแต่ทางหักพอยท์ ไม่มีทางแจก ทำให้เคสชดเชยลูกค้าทำไม่ได้
 * เหตุผลบังคับกรอกเสมอ เพราะทุกการแจกต้องตามย้อนหลังได้
 */
const PointGrantForm = (props) => {
  const { form } = props
  const { getFieldDecorator, validateFields, resetFields } = form
  const dispatch = useDispatch()
  const [submitting, setSubmitting] = useState(false)

  const { userPointList, pointScopeList, isLoading } = useSelector(
    (state) => ({
      userPointList: state.reward.userPointSearchList,
      pointScopeList: state.reward.pointScopeList,
      isLoading: state.reward.isLoading
    }),
    []
  )

  useDeepEffect(() => {
    // โหลดชุดแรกไว้ให้เห็นตัวเลือกก่อนพิมพ์ ที่เหลือค้นจาก server
    dispatch(searchUserPoint(''))
    dispatch(fetchPointScopeList(''))
  }, [])

  // ผู้ใช้หลักหมื่นและกระเป๋าโตตามฮีโร่ × กิจกรรม จึงต้องค้นที่ server ไม่ใช่กรองฝั่ง client
  const debouncedUserSearch = debounce(
    (keyword) => dispatch(searchUserPoint(keyword)),
    400
  )
  const debouncedScopeSearch = debounce(
    (keyword) => dispatch(fetchPointScopeList(keyword)),
    400
  )

  // กระเป๋าพอยท์สะสมเดิมคือค่าเริ่มต้นเมื่อไม่เลือกอะไร
  const legacyScope = (pointScopeList || []).find(
    (scope) => scope.scopeKey === 'LEGACY'
  )

  function scopeLabel(scope) {
    return scope.displayName || scope.scopeKey || `กระเป๋า ${scope.id}`
  }

  function onSubmit(e) {
    e.preventDefault()
    validateFields(async (err, values) => {
      if (err) return

      setSubmitting(true)
      try {
        const payload = {
          userId: Number(values.userId),
          amount: Number(values.amount),
          reason: values.reason
        }
        // ไม่เลือกกระเป๋า = เข้ากระเป๋าพอยท์สะสมเดิม (backend เป็นคนตัดสิน)
        if (values.scopeId) {
          payload.scopeId = Number(values.scopeId)
        }
        await dispatch(grantPointManually(payload))
        message.success('แจกพอยท์สำเร็จ')
        resetFields()
        dispatch(searchUserPoint(''))
      } catch (error) {
        message.error('แจกพอยท์ไม่สำเร็จ')
      } finally {
        setSubmitting(false)
      }
    })
  }

  return (
    <Card title="แจกพอยท์ให้ผู้ใช้">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="ทุกการแจกถูกบันทึกไว้พร้อมเหตุผล"
        description="พอยท์ที่แจกจะเข้ากระเป๋าที่เลือก และมีวันหมดอายุตามนโยบายของกระเป๋านั้น ดูย้อนหลังได้ที่หน้า Point Transaction"
      />
      <Form onSubmit={onSubmit} layout="vertical" style={{ maxWidth: 520 }}>
        <Form.Item label="ผู้ใช้">
          {getFieldDecorator('userId', {
            rules: [{ required: true, message: 'กรุณาเลือกผู้ใช้' }]
          })(
            <Select
              showSearch
              placeholder="พิมพ์ชื่อผู้ใช้เพื่อค้นหา"
              notFoundContent={isLoading ? <Spin size="small" /> : null}
              filterOption={false}
              onSearch={debouncedUserSearch}>
              {(userPointList || []).map((user) => (
                <Select.Option key={user.userId} value={user.userId}>
                  {`${user.userName} (#${user.userId}) — คงเหลือ ${user.currentPoint} พอยท์`}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
        <Form.Item label="จำนวนพอยท์">
          {getFieldDecorator('amount', {
            rules: [
              { required: true, message: 'กรุณากรอกจำนวนพอยท์' },
              {
                validator: (rule, value, callback) => {
                  if (value !== undefined && value !== '' && Number(value) < 1) {
                    callback('จำนวนต้องมากกว่า 0')
                  } else {
                    callback()
                  }
                }
              }
            ]
          })(<Input type="number" min={1} placeholder="เช่น 100" />)}
        </Form.Item>
        <Form.Item label="กระเป๋าปลายทาง">
          {getFieldDecorator('scopeId', {
            initialValue: legacyScope ? legacyScope.id : undefined
          })(
            <Select
              showSearch
              allowClear
              placeholder="พอยท์สะสมเดิม"
              filterOption={false}
              onSearch={debouncedScopeSearch}>
              {(pointScopeList || []).map((scope) => (
                <Select.Option key={scope.id} value={scope.id}>
                  {scopeLabel(scope)}
                </Select.Option>
              ))}
            </Select>
          )}
          <div style={{ color: '#999' }}>
            เว้นว่าง = เข้ากระเป๋าพอยท์สะสมเดิม ซึ่งใช้แลกรางวัลที่ไม่มีเงื่อนไขได้ทุกชิ้น
          </div>
        </Form.Item>
        <Form.Item label="เหตุผล">
          {getFieldDecorator('reason', {
            rules: [{ required: true, message: 'ต้องระบุเหตุผลในการแจกพอยท์' }]
          })(
            <Input.TextArea
              rows={3}
              placeholder="เช่น ชดเชยเคสส่งต่อแล้วระบบไม่ให้พอยท์ บิล #12345"
            />
          )}
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting}>
          แจกพอยท์
        </Button>
      </Form>
    </Card>
  )
}

export default Form.create({ name: 'PointGrantForm' })(PointGrantForm)
